import { formatContactName, parseChildrenIds } from './schoolRecords';

export const STUDENT_PICKUP_STEPS = [
  { key: 'waiting', label: 'Waiting for dismissal' },
  { key: 'called', label: 'Driver on the way' },
  { key: 'arrived', label: 'Driver in pickup zone' },
  { key: 'released', label: 'Student released' },
  { key: 'completed', label: 'Picked up' },
];

const PICKUP_STATUS_INDEX = {
  waiting: 0,
  called: 1,
  on_the_way: 1,
  arrived: 2,
  ready: 2,
  in_zone: 2,
  driver_in_zone: 2,
  released: 3,
  sent: 3,
  picked_up: 4,
  completed: 4,
};

export function getStudentPickupStepIndex(status) {
  return PICKUP_STATUS_INDEX[String(status || 'waiting').toLowerCase()] ?? 0;
}

export function getStudentPickupStatusLabel(status) {
  switch (String(status || 'waiting').toLowerCase()) {
    case 'called':
    case 'on_the_way':
      return 'On the way';
    case 'arrived':
    case 'ready':
    case 'in_zone':
    case 'driver_in_zone':
      return 'In pickup zone';
    case 'released':
    case 'sent':
      return 'Released';
    case 'picked_up':
    case 'completed':
      return 'Picked up';
    case 'waiting':
    default:
      return 'Waiting';
  }
}

export function getStudentPickupStatusTone(status) {
  switch (String(status || 'waiting').toLowerCase()) {
    case 'called':
    case 'on_the_way':
      return 'warning';
    case 'arrived':
    case 'ready':
    case 'in_zone':
    case 'driver_in_zone':
    case 'released':
    case 'sent':
      return 'info';
    case 'picked_up':
    case 'completed':
      return 'success';
    case 'waiting':
    default:
      return 'neutral';
  }
}

export function getStudentPickupUpdatedAt(dismissalStatus) {
  return dismissalStatus?.picked_at
    || dismissalStatus?.sent_at
    || dismissalStatus?.called_at
    || dismissalStatus?.updated_at
    || dismissalStatus?.created_at
    || null;
}

export function getLinkedParentsForStudent(student, parents = []) {
  const studentId = Number(student?.id);
  if (!studentId) return [];

  const guardianIds = new Set(parseChildrenIds(
    student?.guardians_permanent_authorized_driver
    || student?.['guardians/permanent_authorized_driver']
    || []
  ));

  return parents.filter((parent) => (
    parseChildrenIds(parent.children_ids).includes(studentId)
    || guardianIds.has(Number(parent.id))
  ));
}

export function getLinkedDriversForStudent(student, drivers = []) {
  const studentId = Number(student?.id);
  if (!studentId) return [];

  const guardianIds = new Set(parseChildrenIds(
    student?.guardians_permanent_authorized_driver
    || student?.['guardians/permanent_authorized_driver']
    || []
  ).map(Number));

  return drivers.filter((driver) => (
    parseChildrenIds(driver.children_ids).map(Number).includes(studentId)
    || guardianIds.has(Number(driver.id))
  ));
}

export function buildAuthorizedDrivers({ student, linkedParents = [], linkedDrivers = [], tempDrivers = [] }) {
  const drivers = [];
  const seen = new Set();
  const seenContactIds = new Set();

  const pushDriver = (driver) => {
    if (!driver?.name) return;

    const contactId = Number(
      driver.contactData?.id
      || driver.record?.contact?.id
      || driver.record?.authorized_contact_id
    );
    const key = `${String(driver.name).trim().toLowerCase()}::${String(driver.relation || '').trim().toLowerCase()}`;
    if ((Number.isFinite(contactId) && contactId > 0 && seenContactIds.has(contactId)) || seen.has(key)) return;

    if (Number.isFinite(contactId) && contactId > 0) seenContactIds.add(contactId);
    seen.add(key);
    drivers.push(driver);
  };

  linkedParents.forEach((parent) => {
    pushDriver({
      id: `parent-${parent.id}`,
      name: formatContactName(parent),
      relation: parent.type === 'guardian' ? 'Guardian' : 'Parent',
      contactData: parent,
    });
  });

  linkedDrivers.forEach((driver) => {
    pushDriver({
      id: `driver-${driver.id}`,
      name: formatContactName(driver),
      relation: driver.emergency_relationship || 'Authorized Driver',
      contactData: driver,
    });
  });

  if (student?.emergency_contact_f_name || student?.emergency_contact_l_name) {
    pushDriver({
      id: 'emergency-contact',
      name: [student.emergency_contact_f_name, student.emergency_contact_l_name].filter(Boolean).join(' ').trim(),
      relation: student.emergency_contact_relationship || 'Emergency Contact',
    });
  }

  tempDrivers.forEach((driver, index) => {
    pushDriver({
      id: `temp-driver-${driver.id ?? index}`,
      name: driver.driver_name
        || driver.name
        || (driver.contact ? formatContactName(driver.contact) : '')
        || driver.notes
        || `Authorized Driver ${index + 1}`,
      relation: driver.relationship || 'Authorized Driver',
      contactData: driver.contact || null,
      record: driver,
    });
  });

  return drivers;
}
